import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import { EmailLogDetail, EmailLogListParams, EmailLogListResponse, EmailLogResendResult } from '@models/email-logs';
import { Observable } from 'rxjs';

/**
 * Read + resend access to the CRM email log (`EmailMessages` outbox, CS-215).
 *
 * Paging is KEYSET: the list takes an opaque `cursor` (the previous response's `nextCursor`) instead of
 * a page number, because the outbox is append-heavy and an OFFSET would both scan the whole prefix on
 * deep pages and shift rows across page boundaries while new emails are queued. There is deliberately
 * no way to jump to page N.
 */
@Injectable({ providedIn: 'root' })
export class EmailLogsService {
  private readonly http = inject(HttpClient);
  private readonly resourceUrl = `${environment.apiUrl}/api/crm/email-logs`;

  /**
   * One page of the log. Pass `cursor: null` for the first page and echo back `nextCursor` for each
   * following one; `nextCursor === null` in the response means there is nothing after this page.
   * Null/empty filters are omitted so they don't reach the server as empty strings.
   */
  getEmailLogs(params: EmailLogListParams): Observable<EmailLogListResponse> {
    let httpParams = new HttpParams().set('pageSize', params.pageSize);

    const setIf = (key: string, value: string | number | null | undefined) => {
      if (value != null && value !== '') httpParams = httpParams.set(key, value);
    };

    setIf('search', params.search);
    setIf('subject', params.subject);
    // Enums travel as their backend member name (e.g. `BookingConfirmation`), never as a number.
    setIf('logType', params.logType);
    setIf('status', params.status);
    setIf('deliveryStatus', params.deliveryStatus);
    setIf('customer', params.customer);
    setIf('customerId', params.customerId);
    setIf('reservation', params.reservation);
    setIf('reservationId', params.reservationId);
    setIf('createdFrom', params.createdFrom);
    setIf('createdTo', params.createdTo);
    setIf('acceptedFrom', params.acceptedFrom);
    setIf('acceptedTo', params.acceptedTo);
    // Omitted for the first page. A cursor the server can't parse is a 400, not a silent restart, so
    // never synthesize one — only ever send back what a previous response handed out.
    setIf('cursor', params.cursor);

    return this.http.get<EmailLogListResponse>(this.resourceUrl, {
      params: httpParams,
    });
  }

  /** Full email incl. rendered body, delivery timeline and attachment metadata. */
  getEmailLog(id: number): Observable<EmailLogDetail> {
    return this.http.get<EmailLogDetail>(`${this.resourceUrl}/${id}`);
  }

  /**
   * Queues a copy of the email for the address it was originally sent to. Answers `202 Accepted` with
   * the identifiers of the new outbox row — the send itself happens later in the background dispatcher,
   * so a successful response means "queued", not "delivered".
   */
  resendEmailLog(id: number): Observable<EmailLogResendResult> {
    return this.http.post<EmailLogResendResult>(`${this.resourceUrl}/${id}/resend`, {});
  }

  /**
   * Downloads one attachment's bytes. Goes through HttpClient on purpose: the API requires the bearer
   * token that `authInterceptor` attaches, so a plain link to
   * {@link getAttachmentDownloadUrl} would come back 401. Use {@link saveAttachment} unless you need
   * the blob itself.
   */
  downloadAttachment(id: number, attachmentId: number): Observable<Blob> {
    return this.http.get(`${this.resourceUrl}/${id}/attachments/${attachmentId}`, {
      responseType: 'blob',
    });
  }

  /**
   * Absolute URL of the attachment-download endpoint. NOT usable as an `<a href>` / `window.open`
   * target: the request needs an `Authorization` header, which a browser navigation cannot add. Exposed
   * for logging/diagnostics only — to actually offer the file, call {@link saveAttachment}.
   */
  getAttachmentDownloadUrl(id: number, attachmentId: number): string {
    return `${this.resourceUrl}/${id}/attachments/${attachmentId}`;
  }

  /**
   * Fetches the attachment and hands it to the browser's download flow under its original file name.
   * Errors propagate to the caller so it can toast them.
   */
  saveAttachment(id: number, attachmentId: number, fileName: string): Observable<void> {
    return new Observable<void>((subscriber) => {
      const subscription = this.downloadAttachment(id, attachmentId).subscribe({
        next: (blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = fileName;
          link.click();
          // Revoking immediately would race the download in some browsers; one tick is enough for the
          // click to have been picked up.
          setTimeout(() => URL.revokeObjectURL(objectUrl));
          subscriber.next();
          subscriber.complete();
        },
        error: (err) => subscriber.error(err),
      });
      return () => subscription.unsubscribe();
    });
  }
}
